import { fetchStructuredProfile, getMyFlags } from '../services/api';
import { hasRealProfileData } from './profileMeta';

// Where to land right after Google sign-in.
//
// 1) A draft carried from the landing try-hero ALWAYS wins: the user typed
//    their picture (maybe hit the preview limit — "sign in to keep going")
//    and signing in is the continuation of that exact flow. /profile is
//    where the scratchpad adopts and auto-saves it — sending these users to
//    Discover instead read as losing their work (founder-hit, 2026-07-18).
// 2) Otherwise: new students (no real profile yet) start at profile
//    building — step one of the activation path; returning students keep
//    Discover. Any failure to check falls back to Discover: never block a
//    returning user on a flaky profile read.
// 3) EXCEPT on a phone, where step one is /welcome. The profile page opens on
//    a blank textarea, which is the heaviest possible first action on a soft
//    keyboard; /welcome asks one tapped question (grade), then two tapped
//    doors, and pays off with real fit previews. Same activation path, an
//    input method that suits the device (session replays are mostly mobile,
//    founder call 2026-07-29).

/** Coarse phone check. Not a hook — this runs outside React. */
export function isPhoneViewport() {
    try {
        return typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia('(max-width: 767px)').matches;
    } catch {
        return false;          // unknown device → the desktop path, unchanged
    }
}

// Where a signed-out visitor was HEADED when auth interrupted them.
// sessionStorage, not router state: mobile sign-in uses signInWithRedirect,
// which fully reloads the app — react-router state does not survive that
// round-trip, and this must (email pre-flight, 2026-08-04: every lifecycle
// email deep-links a feature, and all three sign-in paths were discarding
// the destination, landing every click on /universities).
export const PENDING_DEST_KEY = 'pending_dest';

// An internal app path, the one test every destination passes. Not '//host'
// and not '/\\host' or '/<tab>/host' either: browsers resolve both to another
// origin (security review, #3928: an emailed sign-in link can carry a
// destination an attacker wrote). No control characters, no whitespace.
const INTERNAL_PATH = /^\/(?![/\\])[^\\\s\x00-\x1f\x7f]{0,300}$/;
export const isInternalPath = (path) => typeof path === 'string' && INTERNAL_PATH.test(path);

export function rememberDestination(path) {
    try {
        // Internal app paths only (open-redirect hygiene).
        if (isInternalPath(path) && path !== '/') {
            window.sessionStorage.setItem(PENDING_DEST_KEY, path);
        }
    } catch { /* private mode */ }
}

function consumeDestination() {
    try {
        const dest = window.sessionStorage.getItem(PENDING_DEST_KEY);
        if (dest) {
            window.sessionStorage.removeItem(PENDING_DEST_KEY);
            if (isInternalPath(dest)) return dest;
        }
    } catch { /* private mode */ }
    return null;
}

// 4) Astra Today (#3754, ADR 0041; founder pick A1): a RETURNING student the
//    astra_today pilot flag is on for lands on /today, Launchpad one tap
//    away. The flag read fails toward the old landing: an error is "not on".
// 7) The parent path (#5811; founder pick A, his answer 5): a returning
//    parent-role account the parent_path flag is on for lands on its home,
//    the parent read of its own list (/home), the Launchpad one tap away. The
//    role is read from the profile the caller already fetched; absent is "not
//    asked", the usual landing.
export async function returningHome(email, profile = null) {
    try {
        const flags = await getMyFlags(email);
        if (flags && flags.parent_path && profile && profile.account_role === 'parent') return '/home';
        if (flags && flags.astra_today) return '/today';
    } catch { /* flags unreadable: the usual landing */ }
    return '/universities';
}

// 5) Profile-first sign-up (#4702; founder approval of the pricing and trial
//    strategy, 2026-10-18): with the profile_first_signup flag on for the
//    account, a new student (no real profile) lands on /start on every
//    device: GPA, a test score or none, an intended major and three colleges,
//    nothing else first, then the list read in the same session. Schools
//    carried from the landing page fill the three colleges there. A typed
//    landing draft still goes to /profile, where it is adopted. The flag read
//    fails toward the old landing: an error is "not on".
export async function profileFirstOn(email) {
    try {
        const flags = await getMyFlags(email);
        return Boolean(flags && flags.profile_first_signup);
    } catch {
        return false;
    }
}

// 6) One onboarding (#5085; founder, 2026-10-21, open question 3: "Yes,
//    route them to /start"): with first_read_onboarding on too, a phone with
//    a typed landing draft goes to /start like everyone else instead of
//    /welcome. /start reads one college first and never clears the draft
//    (only /profile adopts it), so the work is kept. Both flags, because
//    /start sends anyone without profile_first_signup back to /welcome. The
//    read fails toward /welcome: an error is "not on".
export async function firstReadOnboardingOn(email) {
    try {
        const flags = await getMyFlags(email);
        return Boolean(flags && flags.first_read_onboarding && flags.profile_first_signup);
    } catch {
        return false;
    }
}

export async function postSignInDestination(email) {
    if (!email) return '/universities';
    // The interrupted destination wins over every heuristic: the user already
    // told us where they were going.
    const pending = consumeDestination();
    if (pending) return pending;
    let hasDraft = false;
    let typedDraft = false;
    try {
        typedDraft = !!localStorage.getItem('scratchpad_draft_anonymous');
        hasDraft = !!(typedDraft || localStorage.getItem('try_hero_schools_anonymous'));
    } catch { /* private mode — fall through to the profile check */ }
    // Only schools carried (no typed draft) and the profile-first flag on:
    // /start takes the schools, so the profile check below decides.
    let carriedOnly = false;
    if (hasDraft && !typedDraft && await profileFirstOn(email)) { hasDraft = false; carriedOnly = true; }

    // #1507: the draft carryover used to win UNCONDITIONALLY, which meant a
    // phone visitor who touched the landing try-hero (that widget writes
    // try_hero_schools_anonymous) was sent to /profile's blank textarea —
    // the exact soft-keyboard first action /welcome exists to avoid — and
    // 78% of signups are mobile. On a phone the device-appropriate step one
    // wins instead. The draft is NOT lost: nothing clears those keys except
    // /profile itself, which adopts them whenever the student arrives there
    // (/welcome routes on to /profile), so the "don't lose their work"
    // guarantee survives the reorder.
    if (hasDraft && isPhoneViewport()) {
        try {
            const res = await fetchStructuredProfile(email);
            // A returning student with a real profile must never be dropped
            // into onboarding just because a stale draft is sitting there.
            if (hasRealProfileData(res?.profile)) return returningHome(email, res.profile);
            // fetchStructuredProfile NEVER throws — api.js turns a timeout,
            // 5xx or offline into {success:false, error, profile:null}. Only
            // the literal "No profile found" means "new student"; anything
            // else means we do not know, and guessing /welcome would walk a
            // returning student through onboarding and re-type their profile
            // over the real one (reviewer, 2026-08-13).
            if (res?.error && res.error !== 'No profile found') return '/profile';
            // A typed draft on a phone (#4702 keeps it); /start once the
            // first-read onboarding is on for the account (#5085).
            return (await firstReadOnboardingOn(email)) ? '/start' : '/welcome';
        } catch {
            return '/profile';   // read failed → the old behaviour, draft wins
        }
    }
    if (hasDraft) return '/profile';   // desktop: unchanged
    try {
        const res = await fetchStructuredProfile(email);
        if (hasRealProfileData(res?.profile)) return returningHome(email, res.profile);
        // Same rule as the phone branch above: only the literal "No profile
        // found" (or a husk) is a new student; an unreadable profile is not.
        const unreadable = Boolean(res?.error) && res.error !== 'No profile found';
        // Carried schools with an unreadable profile: /profile, as before this
        // flag (2026-08-13: an unreadable profile is never "new").
        if (unreadable && carriedOnly) return '/profile';
        if (!unreadable && await profileFirstOn(email)) return '/start';
        return isPhoneViewport() ? '/welcome' : '/profile';
    } catch {
        return '/universities';
    }
}
